import React, { type ReactNode } from "react";

import { TYPE_STRING } from "@components/Form";
import InputExtra from "@components/Form/InputExtra";

import getPlaceholderValidationRule from "./validateLinkTemplatePlaceholders";

const LABEL_PORTAL_URL = "Portal";
const LABEL_SIGN_IN_URL = "Sign In";
const LABEL_INVESTMENT_URL = "Investment";
const LABEL_DOCUMENTS_URL = "Documents";
const LABEL_INVESTMENT_SIGN_URL = "PPM Signed";
const LABEL_INVESTMENT_SIGN_THANK_YOU_URL = "PPM Signed - Thank You";
const LABEL_SOFT_COMMITMENT_URL = "Soft Commitment";
const EXTRA_PORTAL_URL = "General portal entry for emails that do not target a specific project (password reset, account connected).";
const EXTRA_SIGN_IN_URL = "Login URL for emails that prompt the investor to sign in (e.g. distribution on hold).";
const EXTRA_INVESTMENT_URL = "Deep link to a project's investment flow. Used in investment-related emails. Placeholders: {projectId}.";
const EXTRA_DOCUMENTS_URL = "Deep link to project documents shared with the investor. Placeholders: {projectId}.";
const EXTRA_INVESTMENT_SIGN_URL = "DocuSign return URL for in-portal signing. Handles ?event=signing_complete and ?event=ttl_expired. Placeholders: {projectId}, {investmentId}.";
const EXTRA_INVESTMENT_SIGN_THANK_YOU_URL = "DocuSign return URL for backstage-initiated signing (public thank-you page, no login). Handles ?event=signing_complete and ?event=ttl_expired. Optional placeholders: {projectId}, {investmentId}.";
const EXTRA_SOFT_COMMITMENT_URL = "Public soft-commitment form URL. Placeholders: {projectId}, {sponsorId}.";

type LinkTemplateField = {
  name: string;
  label: string;
  extra: string;
  placeholders?: string[];
};

type LinkTemplateInput = {
  name: string;
  type: string;
  label: string;
  itemProps: {
    extra: ReactNode;
    rules?: ReturnType<typeof getPlaceholderValidationRule>[];
  };
};

const LINK_TEMPLATE_FIELDS: LinkTemplateField[] = [
  { name: "portalUrl", label: LABEL_PORTAL_URL, extra: EXTRA_PORTAL_URL },
  {
    name: "softCommitmentUrl",
    label: LABEL_SOFT_COMMITMENT_URL,
    extra: EXTRA_SOFT_COMMITMENT_URL,
    placeholders: ["{projectId}", "{sponsorId}"],
  },
  { name: "signInUrl", label: LABEL_SIGN_IN_URL, extra: EXTRA_SIGN_IN_URL },
  {
    name: "investmentUrl",
    label: LABEL_INVESTMENT_URL,
    extra: EXTRA_INVESTMENT_URL,
    placeholders: ["{projectId}"],
  },
  {
    name: "documentsUrl",
    label: LABEL_DOCUMENTS_URL,
    extra: EXTRA_DOCUMENTS_URL,
    placeholders: ["{projectId}"],
  },
  {
    name: "investmentSignUrl",
    label: LABEL_INVESTMENT_SIGN_URL,
    extra: EXTRA_INVESTMENT_SIGN_URL,
    placeholders: ["{projectId}", "{investmentId}"],
  },
  {
    name: "investmentSignThankYouUrl",
    label: LABEL_INVESTMENT_SIGN_THANK_YOU_URL,
    extra: EXTRA_INVESTMENT_SIGN_THANK_YOU_URL,
  },
];

const getLinkTemplatesSchema = (): LinkTemplateInput[] =>
  LINK_TEMPLATE_FIELDS.map(({ name, label, extra, placeholders }) => ({
    name,
    type: TYPE_STRING,
    label,
    itemProps: {
      extra: <InputExtra text={extra} hasIcon={false} />,
      ...(placeholders && {
        rules: [getPlaceholderValidationRule(placeholders)],
      }),
    },
  }));

export default getLinkTemplatesSchema;
